"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from "@/lib/utils";
import { Button, Icon, Typography } from "@bookips/sds";
import * as Accordion from "@radix-ui/react-accordion";
import { forwardRef, memo, useEffect, useState } from "react";
import { useScrollLock } from "usehooks-ts";
// ============================================================================
// Utils
// ============================================================================
/**
 * 선택된 카테고리의 부모 ID 찾기
 */ const findParentCategoryId = (selectedId, items)=>{
    for (const item of items){
        if (!item.items) continue;
        for (const child of item.items){
            if (child.categoryId === selectedId) {
                return item.categoryId;
            }
            // 더 깊은 depth 검색
            if (child.items) {
                const found = findParentCategoryId(selectedId, [
                    child
                ]);
                if (found) return item.categoryId;
            }
        }
    }
    return null;
};
// ============================================================================
// Hooks
// ============================================================================
/** ESC 키로 닫기 */ const useEscapeKey = (isOpen, onClose)=>{
    useEffect(()=>{
        const handleKeyDown = (e)=>{
            if (e.key === "Escape" && isOpen) {
                onClose();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return ()=>document.removeEventListener("keydown", handleKeyDown);
    }, [
        isOpen,
        onClose
    ]);
};
/** 선택된 카테고리의 부모 자동 확장 + 기본 확장 아이템 지원 */ const useAutoExpandParent = (selectedCategoryId, items, defaultExpandedItems)=>{
    const [expandedItems, setExpandedItems] = useState(defaultExpandedItems !== null && defaultExpandedItems !== void 0 ? defaultExpandedItems : []);
    useEffect(()=>{
        // defaultExpandedItems가 있으면 우선 사용
        if (defaultExpandedItems && defaultExpandedItems.length > 0) {
            setExpandedItems(defaultExpandedItems);
            return;
        }
        // 아니면 selectedCategoryId의 부모를 찾아서 확장
        if (selectedCategoryId && items.length > 0) {
            const parentId = findParentCategoryId(selectedCategoryId, items);
            if (parentId) {
                setExpandedItems([
                    parentId
                ]);
            }
        }
    }, [
        selectedCategoryId,
        items,
        defaultExpandedItems
    ]);
    return {
        expandedItems,
        setExpandedItems
    };
};
// ============================================================================
// Sub Components
// ============================================================================
/** Backdrop */ const Backdrop = (param)=>/*#__PURE__*/ {
    let { isOpen , onClose  } = param;
    return _jsx("div", {
        className: cn("fixed inset-0 z-[1400] bg-gray-900/50 transition-opacity duration-300", isOpen ? "opacity-100" : "pointer-events-none opacity-0"),
        onClick: onClose,
        "aria-hidden": "true"
    });
};
/** 헤더 */ const DrawerHeader = (param)=>/*#__PURE__*/ {
    let { title , onClose  } = param;
    return _jsxs("div", {
        className: "flex h-14 shrink-0 items-center justify-between border-b border-gray-50 px-5",
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "subtitle1",
                className: "text-primary",
                children: title
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: onClose,
                className: "flex size-5 items-center justify-center text-primary",
                "aria-label": "닫기",
                children: /*#__PURE__*/ _jsx(Icon.Close_2px, {
                    className: "size-5"
                })
            })
        ]
    });
};
/** 전체보기 버튼 */ const ViewAllButton = (param)=>/*#__PURE__*/ {
    let { title , onClick  } = param;
    return _jsx("div", {
        className: "shrink-0 px-5 py-3",
        children: /*#__PURE__*/ _jsxs(Button, {
            variant: "stroke",
            color: "secondary",
            size: "md",
            className: "w-full",
            onClick: onClick,
            children: [
                title,
                " 전체보기"
            ]
        })
    });
};
/** 아코디언 트리거 */ const CategoryAccordionTrigger = /*#__PURE__*/ forwardRef((param, ref)=>{
    let { label , isExpanded , hasChildren , isSelected , onClick  } = param;
    // 하위 카테고리가 없으면 일반 버튼으로 동작
    if (!hasChildren) {
        return /*#__PURE__*/ _jsx("button", {
            type: "button",
            onClick: onClick,
            className: "flex w-full items-center justify-between px-5 py-3",
            children: /*#__PURE__*/ _jsx(Typography, {
                variant: isSelected ? "subtitle1" : "body1",
                className: isSelected ? "text-primary-main" : "text-primary",
                children: label
            })
        });
    }
    return /*#__PURE__*/ _jsx(Accordion.Header, {
        children: /*#__PURE__*/ _jsxs(Accordion.Trigger, {
            ref: ref,
            className: "flex w-full items-center justify-between px-5 py-3",
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: isExpanded ? "subtitle1" : "body1",
                    className: "text-primary",
                    children: label
                }),
                isExpanded ? /*#__PURE__*/ _jsx(Icon.ArrowUp_2px, {
                    className: "size-[18px] text-primary"
                }) : /*#__PURE__*/ _jsx(Icon.ArrowDown_2px, {
                    className: "size-[18px] text-primary"
                })
            ]
        })
    });
});
CategoryAccordionTrigger.displayName = "CategoryAccordionTrigger";
/** 아코디언 콘텐츠 */ const CategoryAccordionContent = /*#__PURE__*/ memo((param)=>/*#__PURE__*/ {
    let { items , selectedCategoryId , selectedCategoryIds , onItemClick  } = param;
    return _jsx(Accordion.Content, {
        className: "overflow-hidden bg-gray-0 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
        children: /*#__PURE__*/ _jsx("div", {
            className: "flex flex-col",
            children: items.map((item)=>{
                // selectedCategoryIds가 있으면 배열에서 확인 (IpSelect용), 아니면 기존 단일 ID 확인
                const isSelected = selectedCategoryIds ? selectedCategoryIds.includes(item.categoryId) : selectedCategoryId === item.categoryId;
                return /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    onClick: ()=>onItemClick(item.categoryId),
                    className: "flex w-full items-center px-5 py-3 text-left",
                    children: /*#__PURE__*/ _jsx(Typography, {
                        variant: isSelected ? "subtitle2" : "body2",
                        className: isSelected ? "text-primary-main" : "text-gray-700",
                        children: item.title
                    })
                }, item.categoryId);
            })
        })
    });
});
CategoryAccordionContent.displayName = "CategoryAccordionContent";
// ============================================================================
// Main Component
// ============================================================================
/**
 * 모바일 카테고리 Drawer 컴포넌트
 * - sm 미만: 전체 화면 (아래에서 위로 슬라이드)
 * - sm 이상: 오른쪽 사이드시트 (width: 360px)
 */ export const MobileCategoryDrawer = (param)=>{
    let { className , isOpen , onClose , title , onViewAll , items , selectedCategoryId , onCategorySelect , defaultExpandedItems , closeOnSelect =true , selectedCategoryIds  } = param;
    const { expandedItems , setExpandedItems  } = useAutoExpandParent(selectedCategoryId, items, defaultExpandedItems);
    const { lock , unlock  } = useScrollLock({
        autoLock: false
    });
    useEffect(()=>{
        if (isOpen) {
            lock();
        } else {
            unlock();
        }
        return ()=>unlock();
    }, [
        isOpen,
        lock,
        unlock
    ]);
    useEscapeKey(isOpen, onClose);
    const handleCategoryClick = (categoryId)=>{
        onCategorySelect(categoryId);
        // closeOnSelect가 true일 때만 자동으로 닫기
        if (closeOnSelect) {
            unlock();
            onClose();
        }
    };
    const handleViewAll = ()=>{
        // 페이지 이동 전에 스크롤 잠금 해제
        unlock();
        onViewAll === null || onViewAll === void 0 ? void 0 : onViewAll();
        onClose();
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Backdrop, {
                isOpen: isOpen,
                onClose: onClose
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: cn("fixed z-[1400] flex flex-col bg-white transition-transform duration-300 ease-out", // 오른쪽 사이드시트 (모든 화면 크기에서 동일)
                "inset-y-0 left-auto right-0 w-full translate-x-full", "sm:w-[360px]", // 열림 상태
                isOpen && "translate-x-0", className),
                role: "dialog",
                "aria-modal": "true",
                "aria-label": title,
                children: [
                    /*#__PURE__*/ _jsx(DrawerHeader, {
                        title: title,
                        onClose: onClose
                    }),
                    onViewAll && /*#__PURE__*/ _jsx(ViewAllButton, {
                        title: title,
                        onClick: handleViewAll
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 overflow-y-auto",
                        children: /*#__PURE__*/ _jsx(Accordion.Root, {
                            type: "multiple",
                            value: expandedItems,
                            onValueChange: setExpandedItems,
                            className: "flex flex-col",
                            children: items.map((category)=>{
                                var _category_items;
                                const hasChildren = !!((_category_items = category.items) === null || _category_items === void 0 ? void 0 : _category_items.length);
                                return /*#__PURE__*/ _jsxs(Accordion.Item, {
                                    value: category.categoryId,
                                    children: [
                                        /*#__PURE__*/ _jsx(CategoryAccordionTrigger, {
                                            label: category.title,
                                            isExpanded: expandedItems.includes(category.categoryId),
                                            hasChildren: hasChildren,
                                            isSelected: false,
                                            onClick: hasChildren ? undefined : ()=>handleCategoryClick(category.categoryId)
                                        }),
                                        hasChildren && /*#__PURE__*/ _jsx(CategoryAccordionContent, {
                                            items: category.items,
                                            selectedCategoryId: selectedCategoryId,
                                            selectedCategoryIds: selectedCategoryIds,
                                            onItemClick: handleCategoryClick
                                        })
                                    ]
                                }, category.categoryId);
                            })
                        })
                    })
                ]
            })
        ]
    });
};
